import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowRight, GraduationCap, Users, Eye, EyeOff } from "lucide-react";
import { LegalLinks, LEGAL_URLS } from "@/components/LegalLinks";
import { GdprNotice } from "@/components/GdprNotice";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, user } = useAuth();
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [role, setRole] = useState("student");
  const [inviteCode, setInviteCode] = useState("");
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [legalError, setLegalError] = useState(false);
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  if (user) {
    navigate(user.role === "instructor" ? "/instructor" : "/games");
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error(t("auth.passwordMin"));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t("auth.passwordMismatch"));
      return;
    }

    if (!acceptedLegal) {
      setLegalError(true);
      toast.error("Debes leer y aceptar la Política de Privacidad y los Términos de Servicio para crear tu cuenta.");
      return;
    }
    setLegalError(false);
    
    setLoading(true);

    try {
      const userData = await register(name, email, password, role, inviteCode, {
        accepted_terms: acceptedLegal,
        marketing_consent: marketingConsent,
      });
      toast.success(`Welcome to SkyBoard, ${userData.name}!`);
      navigate(userData.role === "instructor" ? "/instructor" : "/games");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-bg relative">
      <div className="hero-overlay absolute inset-0" />
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher />
      </div>
      
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo & Title */}
          <div className="text-center mb-8">
            <img src="/skyboard-logo.png" alt="SkyBoard" className="h-64 w-auto mx-auto mb-4" />
            <p className="text-slate-400 mt-2">{t("auth.registerSubtitle")}</p>
          </div>

          {/* Register Card */}
          <Card className="border-0 shadow-2xl">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-heading">{t("auth.register")}</CardTitle>
              <CardDescription>
                {t("auth.registerTitle")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Role Selection */}
                <div className="space-y-3">
                  <Label>{t("auth.role")}</Label>
                  <RadioGroup
                    value={role}
                    onValueChange={setRole}
                    className="grid grid-cols-2 gap-3"
                  >
                    <label
                      className={`flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        role === "student"
                          ? "border-sky-500 bg-sky-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid="role-student-option"
                    >
                      <RadioGroupItem value="student" className="sr-only" />
                      <GraduationCap className={`w-8 h-8 mb-2 ${role === "student" ? "text-sky-500" : "text-slate-400"}`} />
                      <span className={`font-medium ${role === "student" ? "text-sky-700" : "text-slate-600"}`}>
                        {t("auth.student")}
                      </span>
                      <span className="text-xs text-slate-500 mt-1">{t("auth.student")}</span>
                    </label>
                    
                    <label
                      className={`flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        role === "instructor"
                          ? "border-sky-500 bg-sky-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid="role-instructor-option"
                    >
                      <RadioGroupItem value="instructor" className="sr-only" />
                      <Users className={`w-8 h-8 mb-2 ${role === "instructor" ? "text-sky-500" : "text-slate-400"}`} />
                      <span className={`font-medium ${role === "instructor" ? "text-sky-700" : "text-slate-600"}`}>
                        {t("auth.instructor")}
                      </span>
                      <span className="text-xs text-slate-500 mt-1">{t("auth.instructor")}</span>
                    </label>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inviteCode">{t("auth.inviteCode")}</Label>
                  <Input
                    id="inviteCode"
                    type="text"
                    placeholder="XXXX1234"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    required
                    className="h-11 font-mono tracking-widest"
                    data-testid="register-invite-code-input"
                  />
                  <p className="text-xs text-slate-500">{t("auth.inviteCodeHint")}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">{t("auth.name")}</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Smith"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="h-11"
                    data-testid="register-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t("auth.email")}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@university.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11"
                    data-testid="register-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">{t("auth.password")}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="h-11 pr-11"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                      data-testid="toggle-register-password-visibility-btn"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">{t("auth.passwordMin")}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t("auth.confirmPassword")}</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      className={`h-11 pr-11 ${confirmPassword && password !== confirmPassword ? "border-red-400 focus-visible:ring-red-400" : ""}`}
                      data-testid="register-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      aria-label={showConfirmPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                      data-testid="toggle-register-confirm-password-visibility-btn"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-red-500">{t("auth.passwordMismatch")}</p>
                  )}
                </div>

                <GdprNotice />

                <div className="space-y-3">
                  <label
                    className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                      legalError ? "border-red-400 bg-red-50" : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid="accept-legal-label"
                  >
                    <Checkbox
                      checked={acceptedLegal}
                      onCheckedChange={(v) => { setAcceptedLegal(v === true); if (v === true) setLegalError(false); }}
                      className="mt-0.5"
                      data-testid="accept-legal-checkbox"
                    />
                    <span className="text-xs leading-relaxed text-slate-600">
                      He leído y acepto la{" "}
                      <a href={LEGAL_URLS.privacy} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-sky-600 hover:text-sky-700 underline underline-offset-2 font-medium">
                        Política de Privacidad
                      </a>{" "}
                      y los{" "}
                      <a href={LEGAL_URLS.terms} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-sky-600 hover:text-sky-700 underline underline-offset-2 font-medium">
                        Términos de Servicio
                      </a>
                      <span className="text-red-500"> *</span>
                    </span>
                  </label>
                  {legalError && (
                    <p className="text-xs text-red-600" data-testid="accept-legal-error">
                      Debes leer y aceptar la Política de Privacidad y los Términos de Servicio para crear tu cuenta.
                    </p>
                  )}

                  <label
                    className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer transition-colors hover:border-slate-300"
                    data-testid="marketing-consent-label"
                  >
                    <Checkbox
                      checked={marketingConsent}
                      onCheckedChange={(v) => setMarketingConsent(v === true)}
                      className="mt-0.5"
                      data-testid="marketing-consent-checkbox"
                    />
                    <span className="text-xs leading-relaxed text-slate-600">
                      Deseo recibir comunicaciones comerciales sobre novedades y mejoras de SkyBoard (opcional). Puedes
                      retirar este consentimiento en cualquier momento.
                    </span>
                  </label>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t("auth.creatingAccount")}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {t("auth.register")}
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm text-slate-500">
                {t("auth.hasAccount")}{" "}
                <Link
                  to="/login"
                  className="text-sky-600 hover:text-sky-700 font-medium"
                  data-testid="login-link"
                >
                  {t("auth.login")}
                </Link>
              </div>
            </CardContent>
          </Card>
          <LegalLinks />
        </div>
      </div>
    </div>
  );
}
